import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, TextField, Paper, Typography, Divider, FormControlLabel, Slider, Switch, CircularProgress, Tooltip } from '@mui/material';
import { Send as SendIcon, Close as CloseIcon, Settings as SettingsIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../../services/api';
import { useSearchSettings } from '../../contexts/SearchSettingsContext';
import { useHelpContent } from '../../utils/HelpUtils';
import AiSparklesIcon from '../icons/AiSparklesIcon';

const ResizablePaper = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  right: 0,
  top: 64,
  height: 'calc(100vh - 64px)',
  width: '400px',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1000,
  boxShadow: theme.shadows[4],
}));

const ResizeHandle = styled('div')({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '4px',
  cursor: 'ew-resize',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
});

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #e0e0e0',
  position: 'relative',
  zIndex: 1001,
});

const MessagesContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '16px',
});

const InputContainer = styled(Box)({
  padding: '16px',
  borderTop: '1px solid #e0e0e0',
});

const SettingsContainer = styled(Box)({
  padding: '16px',
  borderTop: '1px solid #e0e0e0',
});

const MessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  overflowY: 'auto',
  flexGrow: 1,
}));

const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'type',
})<{ type: 'user' | 'assistant' }>(({ theme, type }) => ({
  padding: theme.spacing(1.5),
  maxWidth: '80%',
  alignSelf: type === 'user' ? 'flex-end' : 'flex-start',
  backgroundColor: type === 'user' ? theme.palette.primary.main : theme.palette.grey[100],
  color: type === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
  borderRadius: theme.spacing(2),
  wordBreak: 'break-word',
}));

const LoadingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  maxWidth: '80%',
  alignSelf: 'flex-start',
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.spacing(2),
}));

const TitleContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const SourceTextsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  borderRadius: theme.spacing(1),
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

interface SemanticSearchWindowProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
}

interface Message {
  type: 'user' | 'assistant';
  content: string;
  sourceTexts?: string[];
}

// Store conversation history in localStorage
const getConversationHistory = (documentId: string): Message[] => {
  const history = localStorage.getItem(`conversation_history_${documentId}`);
  return history ? JSON.parse(history) : [];
};

const saveConversationHistory = (documentId: string, messages: Message[]) => {
  localStorage.setItem(`conversation_history_${documentId}`, JSON.stringify(messages));
};

// Helper to detect RTL (Hebrew, Arabic, etc.)
function isRTL(text: string) {
  const rtlChars = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
  return rtlChars.test(text);
}

const SemanticSearchWindow: React.FC<SemanticSearchWindowProps> = ({ open, onClose, documentId }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [width, setWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { settings, updateSettings } = useSearchSettings();
  const { similarityThreshold, maxResults, autoClearHistory, helpMode, shortTooltips } = settings;
  const { getToolbarTooltipTextForState } = useHelpContent();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);
  const [showSources, setShowSources] = useState(() => {
    const saved = localStorage.getItem('ai_show_sources');
    return saved ? JSON.parse(saved) : false;
  });
  const [showSimilarityScore, setShowSimilarityScore] = useState(() => {
    const saved = localStorage.getItem('ai_show_similarity_score');
    return saved ? JSON.parse(saved) : false;
  });

  // Load conversation history when documentId changes or window opens
  useEffect(() => {
    if (open && documentId) {
      const history = getConversationHistory(documentId);
      setMessages(history);
    } else {
      setMessages([]);
    }
  }, [documentId, open]);

  // Save conversation history when messages change
  useEffect(() => {
    if (documentId && messages.length > 0) {
      saveConversationHistory(documentId, messages);
    }
  }, [documentId, messages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // If showSources is turned off, also turn off showSimilarityScore
  useEffect(() => {
    if (!showSources && showSimilarityScore) {
      setShowSimilarityScore(false);
      localStorage.setItem('ai_show_similarity_score', JSON.stringify(false));
    }
  }, [showSources]);

  const handleResizeStart = (e: React.MouseEvent) => {
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = startWidth + (startX - e.clientX);
      if (newWidth >= 300 && newWidth <= 800) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { type: 'user' as const, content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const requestBody = {
        question: input,
        documentId: documentId,
        includeSources: showSources
      };
      const response = await api.post(`/search/qa`, requestBody);

      const assistantMessage = { 
        type: 'assistant' as const, 
        content: response.data.answer,
        sourceTexts: response.data.sourceTexts 
      };
      setMessages(prev => [...prev, assistantMessage]);

      if (autoClearHistory) {
        setMessages([userMessage, assistantMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { type: 'assistant' as const, content: 'Sorry, there was an error processing your request.' };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearHistory = async () => {
    try {
      await api.post(`/search/qa/clear-history?documentId=${encodeURIComponent(documentId)}`);
      setMessages([]);
      localStorage.removeItem(`conversation_history_${documentId}`);
    } catch (error) {
      console.error('Error clearing conversation history:', error);
    }
  };

   const handleUpdateSettings = async () => {
      try {
        await api.put('/search/settings', {
            search: {
                maxResults,
                similarityThreshold,
                autoClearHistory
            }
        });
      } catch (error) {
        console.error('Error updating settings:', error);
      }
    };

  if (!open) return null;

  return (
    <ResizablePaper style={{ width }}>
      <ResizeHandle
        ref={resizeRef}
        onMouseDown={handleResizeStart}
      />
      <Header>
        <TitleContainer>
          <AiSparklesIcon sx={{ 
            color: (theme: any) => theme.palette.primary.main,
            '& path:first-of-type': {
              stroke: (theme: any) => theme.palette.primary.main
            }
          }} />
          <Typography variant="h6">AI Assistant</Typography>
        </TitleContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-clear-history`}
            title={getToolbarTooltipTextForState('semantic-clear-history', true)}
          >
            <IconButton 
              onClick={handleClearHistory}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-settings`}
            title={getToolbarTooltipTextForState('semantic-settings', true)}
          >
            <IconButton 
              onClick={() => setShowSettings(!showSettings)}
              size="small"
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-close`}
            title={getToolbarTooltipTextForState('semantic-close', true)}
          >
            <IconButton 
              onClick={onClose} 
              size="small"
              sx={{
                color: 'rgba(0, 0, 0, 0.54)',
                '&:hover': {
                  color: 'rgba(0, 0, 0, 0.87)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Header>
      <Divider />
      <MessagesContainer>
        <MessageContainer>
          {messages.map((message, index) => (
            <MessageBubble
              key={index}
              type={message.type}
              dir={isRTL(message.content) ? 'rtl' : 'ltr'}
              sx={{
                textAlign: isRTL(message.content) ? 'right' : 'left',
                whiteSpace: 'pre-line',
              }}
            >
              {message.content}
              {showSources && message.type === 'assistant' && Array.isArray(message.sourceTexts) && message.sourceTexts.length > 0 && (
                <SourceTextsContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Source texts ({message.sourceTexts.length}):
                  </Typography>
                  {message.sourceTexts.map((source: any, idx: number) => {
                    let text: string, subcase: string | undefined, score: number | undefined;
                    if (typeof source === 'string') {
                      text = source;
                      subcase = undefined;
                      score = undefined;
                    } else {
                      text = source.text;
                      subcase = source.subcase;
                      score = source.score;
                    }
                    return (
                      <Box key={idx} sx={{ mb: 2 }}>
                        {subcase && (
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: 'primary.main', display: 'block', mb: 0.5 }}
                            dir={isRTL(subcase) ? 'rtl' : 'ltr'}
                          >
                            {subcase}:
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            p: 1,
                            bgcolor: 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 1,
                            textAlign: isRTL(text) ? 'right' : 'left',
                            whiteSpace: 'pre-line',
                          }}
                          dir={isRTL(text) ? 'rtl' : 'ltr'}
                        >
                          {text}
                        </Typography>
                        {showSimilarityScore && score !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{ color: '#888', display: 'block', mt: 0.5, ml: 1 }}
                          >
                            Score: {(score * 100).toFixed(1)}%
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </SourceTextsContainer>
              )}
            </MessageBubble>
          ))}
          {isLoading && (
            <LoadingIndicator>
              <CircularProgress size={20} />
              <Typography>Generating answer...</Typography>
            </LoadingIndicator>
          )}
          <div ref={messagesEndRef} />
        </MessageContainer>
      </MessagesContainer>
      {showSettings && (
        <SettingsContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2">
              Search Settings
            </Typography>
            <Tooltip 
              key={`${helpMode}-${shortTooltips}-settings-close`}
              title={getToolbarTooltipTextForState('semantic-settings-close', true)}
            >
              <IconButton 
                size="small"
                onClick={() => setShowSettings(false)}
                sx={{ 
                  color: 'rgba(0, 0, 0, 0.54)',
                  '&:hover': {
                    color: 'rgba(0, 0, 0, 0.87)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Tooltip 
              key={`${helpMode}-${shortTooltips}-similarity-threshold`}
              title={getToolbarTooltipTextForState('semantic-similarity-threshold', true)}
            >
              <Typography gutterBottom>
                Similarity Threshold: {similarityThreshold.toFixed(2)}
              </Typography>
            </Tooltip>
            <Slider
              value={similarityThreshold}
              onChange={(_, value) => updateSettings({similarityThreshold: value as number})}
              onChangeCommitted={(_, value) => {
                updateSettings({similarityThreshold: value as number});
                handleUpdateSettings();
              }}
              min={0}
              max={1}
              step={0.01}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Tooltip 
              key={`${helpMode}-${shortTooltips}-max-results`}
              title={getToolbarTooltipTextForState('semantic-max-results', true)}
            >
              <Typography gutterBottom>
                Maximum Results: {maxResults}
              </Typography>
            </Tooltip>
            <Slider
              value={maxResults}
              onChange={(_, value) => updateSettings({maxResults: value as number})}
              onChangeCommitted={(_, value) => {
                updateSettings({maxResults: value as number});
                handleUpdateSettings();
              }}
              min={1}
              max={10}
              step={1}
            />
            <Typography variant="caption" color="text.secondary">
              Maximum context items to send to AI (not used for Find in Document)
            </Typography>
          </Box>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-auto-clear-history`}
            title={getToolbarTooltipTextForState('semantic-auto-clear-history', autoClearHistory)}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={autoClearHistory}
                  onChange={(e) => {
                        updateSettings({ autoClearHistory: e.target.checked });
                        handleUpdateSettings();
                      }
                  }
                />
              }
              label="Auto Clear History"
            />
          </Tooltip>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-show-sources`}
            title={getToolbarTooltipTextForState('semantic-show-sources', showSources)}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showSources}
                  onChange={e => {
                    setShowSources(e.target.checked);
                    localStorage.setItem('ai_show_sources', JSON.stringify(e.target.checked));
                  }}
                />
              }
              label="Show source texts for answers"
            />
          </Tooltip>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-show-similarity-score`}
            title={getToolbarTooltipTextForState('semantic-show-similarity-score', showSimilarityScore)}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showSimilarityScore}
                  onChange={e => {
                    setShowSimilarityScore(e.target.checked);
                    localStorage.setItem('ai_show_similarity_score', JSON.stringify(e.target.checked));
                  }}
                  disabled={!showSources}
                />
              }
              label="Show similarity score for source texts"
              sx={{ ml: 4 }}
            />
          </Tooltip>
        </SettingsContainer>
      )}
      <InputContainer>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-question-input`}
            title={getToolbarTooltipTextForState('semantic-question-input', true)}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Ask a question about the document..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              multiline
              maxRows={4}
            />
          </Tooltip>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-send`}
            title={getToolbarTooltipTextForState('semantic-send', true)}
          >
            <span>
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
              >
                <SendIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </InputContainer>
    </ResizablePaper>
  );
};

export default SemanticSearchWindow; 